/**
 * ComingSoon: the pre-launch placeholder for the platform, routed at its own
 * `/coming-soon` path (`rileconnect.org/coming-soon`).
 *
 * The pre-launch "Coming Soon" page from the approved Figma design (#204,
 * carried into ticket #205, updated per #353's 8/5 EC feedback pass): hero,
 * "where this started", five pillars, "what RILE Connect is not",
 * equity-first analytics, one shared map, ecosystem-of-ecosystems, a
 * mailing-list signup, and a short attribution footer.
 *
 * Routed at `/coming-soon` from App.js — not through `Landing`, and no
 * longer standing in for it at `/` (that ticket #205 arrangement ended with
 * #353). `Landing` is the platform's real marketing page (an "Explore
 * ecosystems" / "Host your ecosystem" pitch against actual hosted
 * ecosystems) and is routed at `/` again in dev so the team can keep
 * developing and testing it; production serves only `/coming-soon` until
 * launch, per #353's routing change. Self-contained (owns its own SEO tags
 * below) so it can be dropped into a route with no wrapper. Carries no nav
 * chrome and no internal links to the rest of the app — see App.js's
 * `isComingSoonPage` NavBar/Footer/banner/ChatWidget suppression.
 */
import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

import SEO from '@/components/common/SEO';
import { RILE_HORIZONTAL_LOGO_WITH_TAG_URL } from '@/shared/utils/brandAssets';

import {
    WHITEPAPER_WIKI_URL,
    WHITEPAPER_DOI,
    WHITEPAPER_DOI_URL,
    C3_LAB_URL,
} from './platformContent';
import WaitlistForm from './WaitlistForm';
import './ComingSoon.css';

const COMING_SOON_OG_IMAGE = '/images/og-rile-connect.png';

const META_DESCRIPTION =
    'RILE Connect is open-source infrastructure that maps the real ' +
    'connections between educators, organizations, and opportunities, ' +
    'making visible who is missing from them.';

// A short accent bar + uppercase kicker label, repeated above several section
// headings in the design. `dark` swaps the label color for the navy sections.
function SectionEyebrow({ label, dark = false }) {
    return (
        <div className="marketing-eyebrow mb-3">
            <span className="marketing-eyebrow-bar" aria-hidden="true" />
            <p
                className={`marketing-eyebrow-label mb-0 ${dark ? 'marketing-eyebrow-label-dark' : ''}`}
            >
                {label}
            </p>
        </div>
    );
}

SectionEyebrow.propTypes = {
    label: PropTypes.string.isRequired,
    dark: PropTypes.bool,
};

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
function HeroSection() {
    return (
        <section className="py-5">
            <Container>
                <Row className="align-items-center g-4">
                    {/* Text column: order-2 (below the logo) on mobile,
                        order-lg-1 (left, first) from lg up. See Bootstrap's
                        column-order utilities: https://getbootstrap.com/docs/5.3/layout/columns/#order-classes */}
                    <Col xs={12} lg={7} className="order-2 order-lg-1">
                        <SectionEyebrow label="Coming soon" />
                        <h1 className="fw-bold marketing-hero-headline mb-4">
                            Open Infrastructure for Learning Ecosystems
                        </h1>
                        <p className="marketing-hero-subhead mb-3">
                            Make the ecosystem visible, including who is missing
                            from it.
                        </p>
                        <p className="marketing-body-muted marketing-body mb-4">
                            RILE Connect is open-source infrastructure that maps
                            the real connections between educators,
                            organizations, and opportunities, making visible
                            who&apos;s missing from them.
                        </p>
                        <Button
                            href="#waitlist"
                            variant="primary"
                            size="lg"
                            className="fw-semibold"
                        >
                            Join the waitlist
                        </Button>
                    </Col>
                    {/* Logo column: order-1 (stacks first) on mobile per
                        design feedback, order-lg-2 (right, second) from lg
                        up, filling the hero's white space at full size. */}
                    <Col
                        xs={12}
                        lg={5}
                        className="order-1 order-lg-2 text-center text-lg-end"
                    >
                        <img
                            src={RILE_HORIZONTAL_LOGO_WITH_TAG_URL}
                            alt="RILE Connect — Ecosystem of Ecosystems"
                            className="marketing-hero-logo img-fluid"
                        />
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Where this started
// ---------------------------------------------------------------------------
function WhereThisStartedSection() {
    return (
        <section className="py-5 bg-light">
            <Container>
                <SectionEyebrow label="Where this started" />
                <Row className="g-4">
                    <Col xs={12} lg={6}>
                        <h2 className="fw-bold marketing-h2 mb-4">
                            Grounded in Community, Scaled for Impact
                        </h2>
                        <p className="marketing-body-muted marketing-body">
                            The Colorado STEM Ecosystem (CO STEM 2.0) built a
                            real, working network of educators, industry
                            partners, and community organizations, first
                            coordinated through a shared spreadsheet and an
                            interactive Kumu map.
                        </p>
                        <p className="marketing-body-muted marketing-body mb-0">
                            It worked, up to a point. Even inside a single
                            institution, it was hard to know what already
                            existed. RILE Connect carries that same open,
                            transparent spirit forward, without the ceiling of a
                            spreadsheet.
                        </p>
                    </Col>
                    <Col xs={12} lg={6}>
                        <div className="h-100 bg-white border rounded-3 p-4">
                            <p className="marketing-callout-label mb-2">
                                WHAT IS RILE?
                            </p>
                            <p className="mb-0">
                                Reciprocal Integrated Learning Ecosystem. An
                                open, decentralized, cloud-enabled
                                infrastructure driving collaboration,
                                accessibility, and innovation across STEM
                                education and the workforce.
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Five pillars
// ---------------------------------------------------------------------------
const PILLAR_CARDS = [
    {
        name: 'Transparency',
        body: 'Connections stay discoverable, not hidden behind proprietary walls. System behavior is fully explainable, not algorithmically opaque.',
    },
    {
        name: 'Trust',
        body: 'Members control what they share. We collect the minimum data required and govern it transparently. Participation is a safe default.',
    },
    {
        name: 'Accessibility',
        body: 'A core foundation, not an afterthought. Built to WCAG 2.1 AA, optimized for mobile, and designed to perform in rural and under-served areas.',
    },
    {
        name: 'Equity',
        body: "Tailored for participants who may not yet have a professional network or vocabulary. Analytics highlight who's absent, not just who's engaged.",
    },
    {
        name: 'Replicability',
        body: 'Fully open-source, modular, and documented. Any region, coalition, or topical community can deploy and customize its own ecosystem.',
    },
];

function FivePillarsSection() {
    return (
        <section className="py-5">
            <Container>
                <SectionEyebrow label="Core pillars" />
                <Row className="mb-4">
                    <Col xs={12} lg={9}>
                        <h2 className="fw-bold marketing-h2 mb-3">
                            Core Pillars of RILE Connect
                        </h2>
                        <p className="marketing-body-muted marketing-body mb-0">
                            Every product decision is guided by five criteria
                            that keep the work durable and impactful.
                        </p>
                    </Col>
                </Row>
                {/* row-cols-lg-5 (Bootstrap's flex-basis grid helper, not the
                    12-column grid: 12 doesn't divide evenly into 5) puts all
                    five side by side in one row from lg up, reading as actual
                    columns rather than the old 3-then-2 stacked-card wrap.
                    Vertical rules between them (marketing-pillar-column's
                    border-start, suppressed on the first) reinforce the
                    "pillar" visual instead of a card grid. */}
                <Row className="row-cols-1 row-cols-sm-2 row-cols-lg-5 g-4 g-lg-0">
                    {PILLAR_CARDS.map((pillar, index) => (
                        <Col key={pillar.name}>
                            <div
                                className={`h-100 text-center text-lg-start px-lg-3 marketing-pillar-column ${
                                    index === 0
                                        ? 'marketing-pillar-column-first'
                                        : ''
                                }`}
                            >
                                <span
                                    className="marketing-pillar-bar d-block d-lg-none mx-auto mx-lg-0"
                                    aria-hidden="true"
                                />
                                <h3 className="h5 fw-bold marketing-pillar-name mb-2">
                                    {pillar.name}
                                </h3>
                                <p className="marketing-body-muted small mb-0">
                                    {pillar.body}
                                </p>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// "Is not" (renamed from "Refuses to be" per 8/5 EC feedback — Janelle:
// softer, more welcoming framing than "refuses").
// ---------------------------------------------------------------------------
const IS_NOT_ITEMS = [
    {
        label: 'A Social Network',
        body: 'No public profile feeds, follower counts, or popularity mechanics. What matters is the relationship between participants and organizations, not personal branding.',
    },
    {
        label: 'A Career Portal',
        body: 'No resume hosting or opaque candidate rankings. Discovery stays transparent, so you can see exactly how and why a mentor or organization connects to you.',
    },
    {
        label: 'A Closed System',
        body: 'Zero vendor lock-in. Data models and source code are entirely open-source, so the work can be independently audited, extended, and replicated by anyone.',
    },
];

// Small "not this" glyph paired with each label below. Strike-through text
// was removed per 8/5 EC feedback: it is not reliably conveyed to screen
// readers (the "not" meaning can be silently dropped), so the negation is
// carried by this explicit X mark instead, with `aria-hidden` on the glyph
// and the word "Not" read aloud via the visually-hidden span beside it.
function NotIcon() {
    return (
        <svg
            className="marketing-not-icon"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
        >
            <path d="M3 3 L13 13 M13 3 L3 13" />
        </svg>
    );
}

function RefusalsSection() {
    return (
        <section className="py-5 marketing-navy-section">
            <Container>
                <SectionEyebrow label="What we are not" dark />
                <Row className="mb-4">
                    <Col xs={12} lg={9}>
                        <h2 className="fw-bold marketing-h2 marketing-h2-dark mb-3">
                            What RILE Connect Is Not
                        </h2>
                        <p className="marketing-navy-muted marketing-body mb-0">
                            Building an equitable platform means being explicit
                            about what it will never become.
                        </p>
                    </Col>
                </Row>
                <Row className="g-4">
                    {IS_NOT_ITEMS.map((item) => (
                        <Col xs={12} md={4} key={item.label}>
                            <p className="marketing-refusal-label mb-2 d-flex align-items-center gap-2">
                                <NotIcon />
                                <span className="visually-hidden">Not: </span>
                                {item.label}
                            </p>
                            <p className="marketing-navy-muted small mb-0">
                                {item.body}
                            </p>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Equity-first analytics: a small decorative network diagram contrasting a
// fully-connected cluster ("present & connected") against a sparse, unlinked
// one ("no representation"). Purely illustrative of a point the paragraph
// beside it already states in full, so it is aria-hidden like the platform
// hero's constellation backdrop.
// ---------------------------------------------------------------------------
const CONNECTED_NODES = [
    { cx: 40, cy: 35 },
    { cx: 75, cy: 15 },
    { cx: 78, cy: 55 },
    { cx: 15, cy: 55 },
];
const CONNECTED_EDGES = [
    [0, 1],
    [0, 2],
    [0, 3],
    [1, 2],
    [2, 3],
];
const ABSENT_NODES = [
    { cx: 260, cy: 20 },
    { cx: 320, cy: 45 },
    { cx: 280, cy: 75 },
];

function EquityNetworkDiagram() {
    return (
        <svg
            className="marketing-network-diagram"
            viewBox="0 0 360 100"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
            focusable="false"
        >
            <g className="marketing-network-connected-edges">
                {CONNECTED_EDGES.map(([from, to]) => {
                    const a = CONNECTED_NODES[from];
                    const b = CONNECTED_NODES[to];
                    return (
                        <line
                            key={`${from}-${to}`}
                            x1={a.cx}
                            y1={a.cy}
                            x2={b.cx}
                            y2={b.cy}
                        />
                    );
                })}
            </g>
            <g className="marketing-network-connected-nodes">
                {CONNECTED_NODES.map((node) => (
                    <circle
                        key={`${node.cx}-${node.cy}`}
                        cx={node.cx}
                        cy={node.cy}
                        r={6}
                    />
                ))}
            </g>
            <text x="12" y="95" className="marketing-network-label">
                present &amp; connected
            </text>

            <g className="marketing-network-absent-nodes">
                {ABSENT_NODES.map((node) => (
                    <circle
                        key={`${node.cx}-${node.cy}`}
                        cx={node.cx}
                        cy={node.cy}
                        r={6}
                    />
                ))}
            </g>
            <text x="248" y="95" className="marketing-network-label">
                no representation
            </text>
        </svg>
    );
}

function EquityAnalyticsSection() {
    return (
        <section className="py-5">
            <Container>
                <SectionEyebrow label="Equity-first analytics" />
                <Row className="align-items-center g-4">
                    <Col xs={12} lg={7}>
                        <h2 className="fw-bold marketing-h2 mb-3">
                            The Most Important Metric Is Who Is Missing
                        </h2>
                        <p className="marketing-body-muted marketing-body mb-0">
                            Traditional platforms measure engagement, tracking
                            who logged in, who clicked, and who returned. RILE
                            Connect tracks those signals too, but an ecosystem
                            built for equity asks the opposite question: which
                            regions have no representation? Which sectors are
                            entirely absent from the network? Tracking these
                            silences turns missing data into targeted outreach
                            and real-world connection.
                        </p>
                    </Col>
                    <Col xs={12} lg={5}>
                        <EquityNetworkDiagram />
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// One shared map
//
// Expanded per 8/5 EC feedback: the original seven chips read as
// COSTEM/STEM-scoped. Broadened to include sectors an EC member specifically
// called out as missing (workforce, sports organizations, policy, informal
// education, university research) so no single hosted ecosystem's makeup
// reads as the template for what a "sector" here means, plus an open-ended
// closer chip so the list itself signals "and more," not a fixed taxonomy.
// ---------------------------------------------------------------------------
const SECTOR_CHIPS = [
    'K-12 Education',
    'Higher Education & University Research',
    'Industry & Business',
    'Workforce Development',
    'Government & Policy',
    'Nonprofits & Foundations',
    'Professional Organizations',
    'Community Coalitions',
    'Sports Organizations',
    'Informal & Out-of-School Education',
];

// Stretch goal (8/5 EC feedback): the chip list above reads as discrete,
// non-overlapping sectors, but in practice an organization often sits inside
// more than one at once (e.g. a university-run workforce program is both
// "Higher Education" and "Workforce Development"). This schematic Venn
// diagram illustrates that directly — three overlapping sectors with a
// single marked node in the shared overlap standing in for one such
// organization. Sample sector labels only (not the full SECTOR_CHIPS list);
// purely illustrative, so it is aria-hidden like this page's other decorative
// network diagrams, with the point spelled out in the caption beside it.
function SectorOverlapDiagram() {
    return (
        <svg
            className="marketing-venn-diagram"
            viewBox="0 0 300 280"
            aria-hidden="true"
            focusable="false"
        >
            <circle
                className="marketing-venn-circle marketing-venn-circle-a"
                cx="130"
                cy="115"
                r="65"
            />
            <circle
                className="marketing-venn-circle marketing-venn-circle-b"
                cx="210"
                cy="115"
                r="65"
            />
            <circle
                className="marketing-venn-circle marketing-venn-circle-c"
                cx="170"
                cy="185"
                r="65"
            />
            <circle
                className="marketing-venn-org-dot"
                cx="170"
                cy="140"
                r="6"
            />
            <text x="40" y="35" className="marketing-venn-label">
                K-12 Education
            </text>
            <text
                x="260"
                y="35"
                className="marketing-venn-label"
                textAnchor="end"
            >
                Workforce Development
            </text>
            <text
                x="170"
                y="270"
                className="marketing-venn-label"
                textAnchor="middle"
            >
                Government &amp; Policy
            </text>
        </svg>
    );
}

function OneSharedMapSection() {
    return (
        <section className="py-5 bg-light">
            <Container>
                <SectionEyebrow label="One shared map" />
                <Row className="mb-4">
                    <Col xs={12} lg={9}>
                        <h2 className="fw-bold marketing-h2 mb-3">
                            One Shared Network, Diverse Sectors
                        </h2>
                        <p className="marketing-body-muted marketing-body mb-0">
                            Hosted ecosystems unite the sectors that shape a
                            person&apos;s path into one open network, not
                            separate isolated lists.
                        </p>
                    </Col>
                </Row>
                <Row className="g-4 align-items-center">
                    <Col xs={12} lg={7}>
                        <div className="d-flex flex-wrap gap-2">
                            {SECTOR_CHIPS.map((sector) => (
                                <span
                                    key={sector}
                                    className="marketing-sector-pill"
                                >
                                    {sector}
                                </span>
                            ))}
                            <span className="marketing-sector-pill marketing-sector-pill-open">
                                Sectors like these and many more — add your own
                            </span>
                        </div>
                    </Col>
                    <Col xs={12} lg={5} className="text-center">
                        <SectorOverlapDiagram />
                        <p className="small marketing-body-muted mb-0 mt-2">
                            Sectors overlap in practice — the marked point shows
                            one organization active in more than one at once.
                        </p>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Ecosystem of ecosystems
//
// Replaced per 8/5 EC feedback: the original three named cards (CO STEM 2.0 /
// Regional Ecosystem / Topical Community) plus a "Your Ecosystem" add-card
// read to the EC as a prescriptive, timeline-like list of ecosystem TYPES —
// Josh and Alyssa both flagged this in the meeting, and we have no live
// tenants of the "Regional"/"Topical" types to point to yet. The new graphic
// drops named/typed examples entirely and instead shows three progressively
// larger network states — one standalone ecosystem, two overlapping (shared
// members sitting in the overlap), several interconnected — which is the
// actual thread Ranjidha described: a person can belong to more than one
// ecosystem, and those cross-ecosystem memberships are what "ecosystem of
// ecosystems" means. Nodes stay abstract (no tenant names) so a viewer sees
// themselves without being told which type of ecosystem they'd be.
// ---------------------------------------------------------------------------
const NETWORK_STATES = [
    {
        key: 'standalone',
        caption: 'One ecosystem',
        detail: 'A single community, on its own.',
    },
    {
        key: 'overlapping',
        caption: 'Two ecosystems, shared members',
        detail: 'People and organizations belong to more than one.',
    },
    {
        key: 'interconnected',
        caption: 'Many ecosystems, one platform',
        detail: 'Shared members link every hosted ecosystem together.',
    },
];

// One standalone cluster: four nodes, no overlap with anything else.
function StandaloneNetworkGraphic() {
    return (
        <svg
            className="marketing-network-state-diagram"
            viewBox="0 0 160 120"
            aria-hidden="true"
            focusable="false"
        >
            <g className="marketing-network-state-edges">
                <line x1="80" y1="35" x2="50" y2="70" />
                <line x1="80" y1="35" x2="110" y2="70" />
                <line x1="50" y1="70" x2="110" y2="70" />
                <line x1="50" y1="70" x2="80" y2="95" />
                <line x1="110" y1="70" x2="80" y2="95" />
            </g>
            <g className="marketing-network-state-nodes">
                <circle cx="80" cy="35" r="7" />
                <circle cx="50" cy="70" r="7" />
                <circle cx="110" cy="70" r="7" />
                <circle cx="80" cy="95" r="7" />
            </g>
        </svg>
    );
}

// Two clusters (blue, amber) with two shared "member" nodes drawn in the
// overlap region and connected into both clusters — the "belongs to more
// than one ecosystem" case.
function OverlappingNetworkGraphic() {
    return (
        <svg
            className="marketing-network-state-diagram"
            viewBox="0 0 220 120"
            aria-hidden="true"
            focusable="false"
        >
            <g className="marketing-network-state-edges">
                {/* left cluster */}
                <line x1="45" y1="30" x2="25" y2="65" />
                <line x1="45" y1="30" x2="65" y2="65" />
                <line x1="25" y1="65" x2="65" y2="65" />
                {/* right cluster */}
                <line x1="175" y1="30" x2="155" y2="65" />
                <line x1="175" y1="30" x2="195" y2="65" />
                <line x1="155" y1="65" x2="195" y2="65" />
                {/* shared members bridging both clusters */}
                <line x1="65" y1="65" x2="95" y2="80" />
                <line x1="25" y1="65" x2="95" y2="80" />
                <line x1="155" y1="65" x2="125" y2="80" />
                <line x1="195" y1="65" x2="125" y2="80" />
                <line x1="95" y1="80" x2="125" y2="80" />
            </g>
            <g className="marketing-network-state-nodes marketing-network-state-nodes-a">
                <circle cx="45" cy="30" r="7" />
                <circle cx="25" cy="65" r="7" />
                <circle cx="65" cy="65" r="7" />
            </g>
            <g className="marketing-network-state-nodes marketing-network-state-nodes-b">
                <circle cx="175" cy="30" r="7" />
                <circle cx="155" cy="65" r="7" />
                <circle cx="195" cy="65" r="7" />
            </g>
            {/* shared member nodes: distinct style marks them as the
                cross-ecosystem link, not a member of either cluster alone */}
            <g className="marketing-network-state-nodes-shared">
                <circle cx="95" cy="80" r="7.5" />
                <circle cx="125" cy="80" r="7.5" />
            </g>
        </svg>
    );
}

// Several clusters with shared-member nodes crossing between every pair —
// the mature "ecosystem of ecosystems" state.
function InterconnectedNetworkGraphic() {
    const clusters = [
        { cx: 45, cy: 30 },
        { cx: 175, cy: 30 },
        { cx: 45, cy: 95 },
        { cx: 175, cy: 95 },
    ];
    const shared = [
        { cx: 110, cy: 30 },
        { cx: 110, cy: 95 },
        { cx: 65, cy: 62 },
        { cx: 155, cy: 62 },
        { cx: 110, cy: 62 },
    ];
    return (
        <svg
            className="marketing-network-state-diagram"
            viewBox="0 0 220 125"
            aria-hidden="true"
            focusable="false"
        >
            <g className="marketing-network-state-edges">
                <line x1="45" y1="30" x2="110" y2="30" />
                <line x1="110" y1="30" x2="175" y2="30" />
                <line x1="45" y1="95" x2="110" y2="95" />
                <line x1="110" y1="95" x2="175" y2="95" />
                <line x1="45" y1="30" x2="65" y2="62" />
                <line x1="45" y1="95" x2="65" y2="62" />
                <line x1="175" y1="30" x2="155" y2="62" />
                <line x1="175" y1="95" x2="155" y2="62" />
                <line x1="65" y1="62" x2="110" y2="62" />
                <line x1="155" y1="62" x2="110" y2="62" />
                <line x1="110" y1="30" x2="110" y2="62" />
                <line x1="110" y1="95" x2="110" y2="62" />
            </g>
            <g className="marketing-network-state-nodes">
                {clusters.map((n) => (
                    <circle key={`${n.cx}-${n.cy}`} cx={n.cx} cy={n.cy} r="7" />
                ))}
            </g>
            <g className="marketing-network-state-nodes-shared">
                {shared.map((n) => (
                    <circle
                        key={`${n.cx}-${n.cy}`}
                        cx={n.cx}
                        cy={n.cy}
                        r="6.5"
                    />
                ))}
            </g>
        </svg>
    );
}

const NETWORK_GRAPHICS = {
    standalone: StandaloneNetworkGraphic,
    overlapping: OverlappingNetworkGraphic,
    interconnected: InterconnectedNetworkGraphic,
};

function EcosystemOfEcosystemsSection() {
    return (
        <section className="py-5 marketing-navy-section">
            <Container>
                <SectionEyebrow label="One platform, many ecosystems" dark />
                <Row className="mb-4">
                    <Col xs={12} lg={9}>
                        <h2 className="fw-bold marketing-h2 marketing-h2-dark mb-3">
                            An Ecosystem of Ecosystems
                        </h2>
                        <p className="marketing-navy-muted marketing-body mb-0">
                            RILE Connect is shared infrastructure, not a single
                            network. Each independent community, a school,
                            sports league, workplace, or hobby group, keeps its
                            own members, network, and map — and the same person
                            or organization can belong to more than one. Those
                            shared memberships are the thread that connects
                            every hosted ecosystem together.
                        </p>
                    </Col>
                </Row>
                <Row className="g-4">
                    {NETWORK_STATES.map(({ key, caption, detail }) => {
                        const Graphic = NETWORK_GRAPHICS[key];
                        return (
                            <Col
                                xs={12}
                                md={4}
                                key={key}
                                className="text-center"
                            >
                                <div className="marketing-network-state-card h-100 p-4 rounded-3">
                                    <Graphic />
                                    <p className="mb-1 fw-semibold marketing-network-state-caption">
                                        {caption}
                                    </p>
                                    <p className="mb-0 small marketing-navy-muted">
                                        {detail}
                                    </p>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
                <div className="marketing-ecosystem-strip text-center rounded-3 py-2 mt-4">
                    <p className="mb-0 small">
                        RILE CONNECT — SHARED INFRASTRUCTURE
                    </p>
                </div>
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Waitlist form
// ---------------------------------------------------------------------------
function WaitlistFormSection() {
    return (
        <section id="waitlist" className="py-5 bg-light">
            <Container>
                <SectionEyebrow label="Get early access" />
                <h2 className="fw-bold marketing-h2 mb-4">Stay Updated</h2>
                <WaitlistForm />
            </Container>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Footer
//
// Attribution only — no copyright line. App.js already renders the shared
// site `Footer` (with its own dynamic-year copyright) directly below every
// route's content, this one included, so a second copyright line here would
// both duplicate it and go stale (see #205 review).
// ---------------------------------------------------------------------------
// The two whitepaper links are deliberately different targets: the wiki is the
// working document COSTEM invites comment on, so it is what "read" points at,
// while the DOI resolves to a pinned revision and is the citable form. Both
// belong here because this is the only page pre-launch traffic reaches.
function MarketingFooter() {
    return (
        <footer className="py-4 border-top">
            <Container>
                <p className="small marketing-body-muted mb-0">
                    Built by student researchers and developers at the{' '}
                    <a
                        href={C3_LAB_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        C3 Lab
                    </a>
                    , MSU Denver&apos;s Department of Computer Science,
                    alongside the leadership team of the Colorado STEM
                    Ecosystem.{' '}
                    <a
                        href={WHITEPAPER_WIKI_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Read the foundational whitepaper
                    </a>
                    ,{' '}
                    <a
                        href={WHITEPAPER_DOI_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        doi:{WHITEPAPER_DOI}
                    </a>
                    .
                </p>
            </Container>
        </footer>
    );
}

export default function ComingSoon() {
    return (
        <>
            {/* isHomePage omitted (#353): the WebSite/Organization JSON-LD
                schemas it emits belong to the platform's real homepage —
                `Landing` at `/`, which sets isHomePage itself — not this
                pre-launch stand-in at `/coming-soon`. */}
            <SEO
                title="RILE Connect"
                description={META_DESCRIPTION}
                canonical="/coming-soon"
                image={COMING_SOON_OG_IMAGE}
            />
            <HeroSection />
            <WhereThisStartedSection />
            <FivePillarsSection />
            <RefusalsSection />
            <EquityAnalyticsSection />
            <OneSharedMapSection />
            <EcosystemOfEcosystemsSection />
            <WaitlistFormSection />
            <MarketingFooter />
        </>
    );
}
